import React, { useState, useEffect, useCallback } from 'react';
import { FaPlus, FaEdit, FaSearch, FaToggleOn, FaToggleOff } from 'react-icons/fa';
import { toast } from 'react-toastify';
import CatalogService, { CatalogItem, CatalogItemKind } from '../services/CatalogService';
import CatalogItemModal from './CatalogItemModal';
import PrimaryButton from '../../../components/PrimaryButton';

export default function CatalogTab(): React.JSX.Element {
  const [items, setItems] = useState<CatalogItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [kindFilter, setKindFilter] = useState<'' | CatalogItemKind>('');
  const [showInactive, setShowInactive] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<CatalogItem | null>(null);

  const loadItems = useCallback(async () => {
    setLoading(true);
    try {
      const params: { search?: string; kind?: CatalogItemKind; is_active?: boolean } = {};
      if (searchTerm) params.search = searchTerm;
      if (kindFilter) params.kind = kindFilter;
      if (!showInactive) params.is_active = true;

      const response = await CatalogService.search(params);
      setItems(response.results);
    } catch (err) {
      console.error('Failed to load catalog items:', err);
      toast.error('Failed to load catalog items');
    } finally {
      setLoading(false);
    }
  }, [searchTerm, kindFilter, showInactive]);

  useEffect(() => {
    loadItems();
  }, [loadItems]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      loadItems();
    }
  };

  const handleCreate = () => {
    setEditingItem(null);
    setModalOpen(true);
  };

  const handleEdit = (item: CatalogItem) => {
    setEditingItem(item);
    setModalOpen(true);
  };

  const handleToggleActive = async (item: CatalogItem, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await CatalogService.update(item.id, { is_active: !item.is_active });
      toast.success(item.is_active ? 'Item deactivated' : 'Item activated');
      loadItems();
    } catch (err) {
      console.error('Failed to toggle item status:', err);
      toast.error('Failed to update item');
    }
  };

  const handleModalClose = () => {
    setModalOpen(false);
    setEditingItem(null);
  };

  const handleModalSave = () => {
    setModalOpen(false);
    setEditingItem(null);
    loadItems();
  };

  const kindOptions = CatalogService.getKindOptions();

  return (
    <div style={{ padding: '24px' }}>
      {/* Header */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '24px',
        }}
      >
        <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 600, color: '#021a4a' }}>
          Catalog ({items.length})
        </h3>
        <PrimaryButton onClick={handleCreate} icon={<FaPlus size={12} />} style={{ padding: '10px 20px' }}>
          New item
        </PrimaryButton>
      </div>

      {/* Filters */}
      <div
        style={{
          display: 'flex',
          gap: '16px',
          marginBottom: '24px',
          flexWrap: 'wrap',
          alignItems: 'flex-end',
        }}
      >
        <div style={{ flex: '1', minWidth: '200px' }}>
          <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: 600, color: '#666' }}>
            Search
          </label>
          <div style={{ position: 'relative' }}>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="SKU, Name..."
              style={{
                width: '100%',
                padding: '10px 12px 10px 38px',
                border: '1px solid #e0e3ef',
                borderRadius: '6px',
                fontSize: '14px',
              }}
            />
            <FaSearch
              style={{
                position: 'absolute',
                left: '12px',
                top: '50%',
                transform: 'translateY(-50%)',
                color: '#999',
              }}
            />
          </div>
        </div>

        <div style={{ minWidth: '150px' }}>
          <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: 600, color: '#666' }}>
            Category
          </label>
          <select
            value={kindFilter}
            onChange={(e) => setKindFilter(e.target.value as '' | CatalogItemKind)}
            style={{
              width: '100%',
              padding: '10px 12px',
              border: '1px solid #e0e3ef',
              borderRadius: '6px',
              fontSize: '14px',
              background: 'white',
            }}
          >
            <option value="">All categories</option>
            {kindOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>

        <div style={{ minWidth: '150px' }}>
          <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: 600, color: '#666' }}>
            Status
          </label>
          <button
            onClick={() => setShowInactive(!showInactive)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '10px 16px',
              border: '1px solid #e0e3ef',
              borderRadius: '6px',
              background: showInactive ? '#f0f0f0' : 'white',
              fontSize: '14px',
              cursor: 'pointer',
              color: '#333',
            }}
          >
            {showInactive ? <FaToggleOn color="#021a4a" /> : <FaToggleOff color="#999" />}
            Show inactive
          </button>
        </div>
      </div>

      {/* Table */}
      {loading ? (
        <div style={{ padding: '40px', textAlign: 'center', color: '#666' }}>
          Loading...
        </div>
      ) : items.length === 0 ? (
        <div
          style={{
            padding: '60px',
            textAlign: 'center',
            color: '#888',
            background: '#f8f9fc',
            borderRadius: '8px',
          }}
        >
          <p style={{ margin: 0, fontSize: '16px' }}>No catalog items found</p>
          <p style={{ margin: '8px 0 0 0', fontSize: '14px' }}>
            Create a new item or adjust your search criteria.
          </p>
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '900px' }}>
            <thead>
              <tr style={{ background: '#f8f9fc', borderBottom: '2px solid #e0e3ef' }}>
                <th style={thStyle}>SKU</th>
                <th style={thStyle}>Category</th>
                <th style={thStyle}>Name</th>
                <th style={thStyle}>Unit</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Purchase (net)</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Markup %</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Sales price (net)</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>VAT %</th>
                <th style={{ ...thStyle, width: '80px', textAlign: 'center' }}>Active</th>
                <th style={{ ...thStyle, width: '80px', textAlign: 'center' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr
                  key={item.id}
                  style={{
                    borderBottom: '1px solid #e0e3ef',
                    cursor: 'pointer',
                    opacity: item.is_active ? 1 : 0.6,
                  }}
                  onClick={() => handleEdit(item)}
                >
                  <td style={tdStyle}>
                    <span style={{ fontFamily: 'monospace', fontSize: '13px' }}>
                      {item.item_number}
                    </span>
                  </td>
                  <td style={tdStyle}>
                    <span
                      style={{
                        padding: '4px 10px',
                        borderRadius: '12px',
                        fontSize: '12px',
                        fontWeight: 600,
                        background: getKindColor(item.kind).bg,
                        color: getKindColor(item.kind).text,
                      }}
                    >
                      {CatalogService.getKindLabel(item.kind)}
                    </span>
                  </td>
                  <td style={tdStyle}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{ fontWeight: 600, color: '#021a4a' }}>{item.name}</span>
                      {/* FE-BILL-HF-R30-4: Scope items count badge */}
                      {(item.scope_items_count ?? 0) > 0 && (
                        <span
                          data-testid={`catalog-scope-count-${item.id}`}
                          title={`${item.scope_items_count} Subtask(s)`}
                          style={{
                            padding: '2px 6px',
                            borderRadius: '10px',
                            fontSize: '11px',
                            fontWeight: 600,
                            background: '#fef3c7',
                            color: '#92400e',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          🧾 {item.scope_items_count}
                        </span>
                      )}
                    </div>
                    {item.description && (
                      <div style={{ fontSize: '12px', color: '#888', marginTop: '2px', maxWidth: '250px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {item.description}
                      </div>
                    )}
                  </td>
                  <td style={tdStyle}>{item.unit}</td>
                  <td style={{ ...tdStyle, textAlign: 'right', fontFamily: 'monospace' }}>
                    {item.purchase_price ? CatalogService.formatPrice(item.purchase_price) : '-'}
                  </td>
                  <td style={{ ...tdStyle, textAlign: 'right', fontFamily: 'monospace' }}>
                    {item.default_markup_percent ? `${CatalogService.formatDecimal(item.default_markup_percent, 1)}%` : '-'}
                  </td>
                  <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 600, fontFamily: 'monospace' }}>
                    {CatalogService.formatPrice(item.sales_price)}
                  </td>
                  <td style={{ ...tdStyle, textAlign: 'right', fontFamily: 'monospace' }}>
                    {item.tax_rate ? `${CatalogService.formatDecimal(item.tax_rate, 1)}%` : '-'}
                  </td>
                  <td style={{ ...tdStyle, textAlign: 'center' }}>
                    <button
                      onClick={(e) => handleToggleActive(item, e)}
                      title={item.is_active ? 'Deactivate' : 'Activate'}
                      style={{
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        padding: '4px',
                      }}
                    >
                      {item.is_active ? (
                        <FaToggleOn size={20} color="#059669" />
                      ) : (
                        <FaToggleOff size={20} color="#999" />
                      )}
                    </button>
                  </td>
                  <td style={{ ...tdStyle, textAlign: 'center' }}>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEdit(item);
                      }}
                      title="Edit"
                      style={actionBtnStyle}
                    >
                      <FaEdit size={14} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal */}
      {modalOpen && (
        <CatalogItemModal
          item={editingItem}
          onClose={handleModalClose}
          onSave={handleModalSave}
        />
      )}
    </div>
  );
}

function getKindColor(kind: CatalogItemKind): { bg: string; text: string } {
  const colors: Record<CatalogItemKind, { bg: string; text: string }> = {
    SERVICE: { bg: '#dbeafe', text: '#1d4ed8' },
    MATERIAL: { bg: '#fef3c7', text: '#92400e' },
    FLAT_FEE: { bg: '#dcfce7', text: '#166534' },
    TEXT: { bg: '#f3e8ff', text: '#7c3aed' },
  };
  return colors[kind] || { bg: '#f0f0f0', text: '#666' };
}

const thStyle: React.CSSProperties = {
  padding: '12px 16px',
  textAlign: 'left',
  fontSize: '13px',
  fontWeight: 600,
  color: '#666',
};

const tdStyle: React.CSSProperties = {
  padding: '14px 16px',
  fontSize: '14px',
  color: '#333',
};

const actionBtnStyle: React.CSSProperties = {
  background: 'none',
  border: 'none',
  padding: '6px',
  cursor: 'pointer',
  color: '#666',
  borderRadius: '4px',
};
